import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonIcon from '@mui/icons-material/Person';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type FC, useMemo, useState } from 'react';
import { useBciGetBrandsModelsQuery, useBciGetVehicleDataMutation } from 'services/api/bciSeguros.api';
import { useValidateRutMutation } from 'services/api/insuranceQuoteForm.api';
import type { BciSegurosFormData } from 'services/types/bciSegurosForm';
import { isValidChileanPhone, normalizeChileanPhone, parseRut } from 'utils/bciSegurosFormsHelpers';

import { inlineActionButtonSx, NAVY, primaryButtonSx, sectionSx, sectionTitleSx } from '../styleConstants';

interface Props {
  formData: BciSegurosFormData;
  onFormDataChange: (updates: Partial<BciSegurosFormData>) => void;
  onNext: () => void;
  setIsLoading: (loading: boolean) => void;
}

const VehicleAndInsuredStep: FC<Props> = ({ formData, onFormDataChange, onNext, setIsLoading }) => {
  // Vehicle state
  const [licensePlate, setLicensePlate] = useState(formData.vehicle.licensePlate);
  const [licensePlateError, setLicensePlateError] = useState<string | null>(null);
  const [vehicleFound, setVehicleFound] = useState(false);

  // Insured state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rutError, setRutError] = useState<string | null>(null);
  const [personFound, setPersonFound] = useState(false);

  const { data: brandsModels = [] } = useBciGetBrandsModelsQuery();
  const [getVehicleData] = useBciGetVehicleDataMutation();
  const [validateRut] = useValidateRutMutation();

  const selectedBrand = useMemo(
    () => brandsModels.find((m) => m.int_IdMarca === formData.vehicle.brandId) || null,
    [brandsModels, formData.vehicle.brandId]
  );
  const models = selectedBrand?.lst_Modelos || [];

  const updateInsured = (updates: Partial<BciSegurosFormData['insured']>) => {
    onFormDataChange({ insured: { ...formData.insured, ...updates } });
  };

  const handleLookupLicensePlate = async () => {
    if (!licensePlate.trim()) return;
    setIsLoading(true);
    setLicensePlateError(null);
    try {
      const result = await getVehicleData({ str_Patente: licensePlate.trim().toUpperCase() }).unwrap();
      const brand = brandsModels.find((m) => m.int_IdMarca === result.int_CodMarcaBci);
      const model = brand?.lst_Modelos.find((m) => m.int_IdModelo === result.int_CodModeloBci);
      onFormDataChange({
        vehicle: {
          ...formData.vehicle,
          licensePlate: licensePlate.trim().toUpperCase(),
          brandId: result.int_CodMarcaBci,
          brandName: result.str_Marca,
          modelId: result.int_CodModeloBci,
          modelName: model?.str_modelo || result.str_Modelo,
          year: result.int_Ano,
          engine: result.str_Motor,
          chassis: result.str_Chasis,
          color: result.str_Color,
          vehicleType: 1,
        },
      });
      setVehicleFound(true);
    } catch {
      setVehicleFound(false);
      setLicensePlateError('No se encontraron datos. Ingresa manualmente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRutChange = (value: string) => {
    const { rut, dv } = parseRut(value);
    updateInsured({ rutFormatted: value, rut, dv });
    setErrors((prev) => ({ ...prev, rut: '' }));
    setRutError(null);
    setPersonFound(false);
  };

  const handleLookupRut = async () => {
    const rutValue = formData.insured.rutFormatted;
    if (!rutValue.trim()) return;
    setIsLoading(true);
    setRutError(null);
    try {
      const result = await validateRut({ rut: rutValue }).unwrap();
      if (result.personInfo) {
        updateInsured({
          firstName: result.personInfo.firstName,
          lastName: result.personInfo.lastName,
          secondLastName: result.personInfo.motherLastName ?? '',
        });
        setPersonFound(true);
      } else {
        setRutError('No se encontraron datos. Ingresa manualmente.');
      }
    } catch {
      setRutError('No se encontraron datos. Ingresa manualmente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    // Vehicle validation
    if (!formData.vehicle.brandId) newErrors.brand = 'Marca es requerida';
    if (!formData.vehicle.modelId) newErrors.model = 'Modelo es requerido';
    if (!formData.vehicle.year) newErrors.year = 'Año es requerido';
    // Insured validation
    if (!formData.insured.rut) newErrors.rut = 'RUT es requerido';
    if (!formData.insured.age) newErrors.age = 'Edad es requerida';
    if (!formData.insured.firstName.trim()) newErrors.firstName = 'Requerido';
    if (!formData.insured.lastName.trim()) newErrors.lastName = 'Requerido';
    if (!formData.insured.email.trim()) newErrors.email = 'Requerido';
    if (!formData.insured.phone.trim()) {
      newErrors.phone = 'Requerido';
    } else if (!isValidChileanPhone(formData.insured.phone)) {
      newErrors.phone = '9 dígitos';
    }

    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    onFormDataChange({ vehicle: { ...formData.vehicle, licensePlate: licensePlate.trim().toUpperCase() } });
    onNext();
  };

  return (
    <Box>
      {/* ── VEHÍCULO — un solo box ── */}
      <Box sx={sectionSx}>
        <Typography
          sx={{ fontWeight: 700, color: NAVY, fontSize: '1rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <DirectionsCarIcon sx={{ fontSize: 20 }} />
          Vehículo
        </Typography>

        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Buscar por patente
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
          <TextField
            label="Patente (opcional)"
            placeholder="ABCD12"
            value={licensePlate}
            onChange={(e) => {
              setLicensePlate(e.target.value.toUpperCase());
              setLicensePlateError(null);
            }}
            error={!!licensePlateError}
            helperText={licensePlateError}
            sx={{ maxWidth: 240 }}
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => {
              handleLookupLicensePlate().catch(() => {});
            }}
            disabled={!licensePlate.trim()}
            sx={{ ...inlineActionButtonSx, mt: '1px' }}
          >
            Buscar
          </Button>
          {vehicleFound && (
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
              label={`${formData.vehicle.brandName} ${formData.vehicle.modelName} ${formData.vehicle.year}`}
              sx={{
                bgcolor: 'rgba(37, 97, 111, 0.08)',
                color: '#25616F',
                fontWeight: 600,
                fontSize: '0.8rem',
                height: 32,
                borderRadius: '8px',
                '& .MuiChip-icon': { color: '#25616F' },
              }}
            />
          )}
        </Box>

        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Tipo y uso
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo Vehículo</InputLabel>
            <Select
              value={formData.vehicle.vehicleType}
              label="Tipo Vehículo"
              onChange={(e) =>
                onFormDataChange({ vehicle: { ...formData.vehicle, vehicleType: e.target.value as 1 | 2 } })
              }
            >
              <MenuItem value={1}>Usado</MenuItem>
              <MenuItem value={2}>Nuevo</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Uso Vehículo</InputLabel>
            <Select
              value={formData.vehicle.vehicleUsage}
              label="Uso Vehículo"
              onChange={(e) =>
                onFormDataChange({ vehicle: { ...formData.vehicle, vehicleUsage: e.target.value as 1 | 2 } })
              }
            >
              <MenuItem value={1}>Comercial</MenuItem>
              <MenuItem value={2}>Particular</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Información del vehículo
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <Autocomplete
            options={brandsModels}
            getOptionLabel={(option) => option.str_Marca}
            value={selectedBrand}
            onChange={(_e, newValue) =>
              onFormDataChange({
                vehicle: {
                  ...formData.vehicle,
                  brandId: newValue?.int_IdMarca || null,
                  brandName: newValue?.str_Marca || '',
                  modelId: null,
                  modelName: '',
                },
              })
            }
            renderInput={(params) => <TextField {...params} label="Marca" size="small" error={!!errors.brand} />}
          />
          <Autocomplete
            options={models}
            getOptionLabel={(option) => option.str_modelo}
            value={models.find((m) => m.int_IdModelo === formData.vehicle.modelId) || null}
            onChange={(_e, newValue) =>
              onFormDataChange({
                vehicle: {
                  ...formData.vehicle,
                  modelId: newValue?.int_IdModelo || null,
                  modelName: newValue?.str_modelo || '',
                },
              })
            }
            renderInput={(params) => <TextField {...params} label="Modelo" size="small" error={!!errors.model} />}
            disabled={!formData.vehicle.brandId}
          />
          <TextField
            label="Año"
            type="number"
            value={formData.vehicle.year ?? ''}
            onChange={(e) =>
              onFormDataChange({ vehicle: { ...formData.vehicle, year: Number(e.target.value) || null } })
            }
            size="small"
            error={!!errors.year}
          />
          <TextField
            label="Motor"
            value={formData.vehicle.engine}
            onChange={(e) => onFormDataChange({ vehicle: { ...formData.vehicle, engine: e.target.value } })}
            size="small"
          />
          <TextField
            label="Chasis"
            value={formData.vehicle.chassis}
            onChange={(e) => onFormDataChange({ vehicle: { ...formData.vehicle, chassis: e.target.value } })}
            size="small"
          />
          <TextField
            label="Color"
            value={formData.vehicle.color}
            onChange={(e) => onFormDataChange({ vehicle: { ...formData.vehicle, color: e.target.value } })}
            size="small"
          />
        </Box>
      </Box>

      {/* ── ASEGURADO — un solo box ── */}
      <Box sx={sectionSx}>
        <Typography
          sx={{ fontWeight: 700, color: NAVY, fontSize: '1rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <PersonIcon sx={{ fontSize: 20 }} />
          Asegurado
        </Typography>

        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Buscar por RUT
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
          <TextField
            label="RUT"
            placeholder="12345678-9"
            value={formData.insured.rutFormatted}
            onChange={(e) => handleRutChange(e.target.value)}
            error={!!errors.rut || !!rutError}
            helperText={errors.rut || rutError}
            sx={{ maxWidth: 240 }}
            size="small"
          />
          <Button
            variant="contained"
            onClick={() => {
              handleLookupRut().catch(() => {});
            }}
            disabled={!formData.insured.rutFormatted.trim()}
            sx={{ ...inlineActionButtonSx, mt: '1px' }}
          >
            Buscar
          </Button>
          {personFound && (
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
              label={`${formData.insured.firstName} ${formData.insured.lastName}${formData.insured.secondLastName ? ` ${formData.insured.secondLastName}` : ''}`}
              sx={{
                bgcolor: 'rgba(37, 97, 111, 0.08)',
                color: '#25616F',
                fontWeight: 600,
                fontSize: '0.8rem',
                height: 32,
                borderRadius: '8px',
                '& .MuiChip-icon': { color: '#25616F' },
              }}
            />
          )}
        </Box>

        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Datos personales
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 2.5 }}>
          <TextField
            label="Edad"
            type="number"
            value={formData.insured.age ?? ''}
            onChange={(e) => {
              updateInsured({ age: Number(e.target.value) || null });
              setErrors((p) => ({ ...p, age: '' }));
            }}
            error={!!errors.age}
            size="small"
            slotProps={{ htmlInput: { min: 18, max: 99 } }}
          />
          <TextField
            label="Nombres"
            value={formData.insured.firstName}
            onChange={(e) => {
              updateInsured({ firstName: e.target.value });
              setErrors((p) => ({ ...p, firstName: '' }));
            }}
            error={!!errors.firstName}
            size="small"
          />
          <TextField
            label="Apellido Paterno"
            value={formData.insured.lastName}
            onChange={(e) => {
              updateInsured({ lastName: e.target.value });
              setErrors((p) => ({ ...p, lastName: '' }));
            }}
            error={!!errors.lastName}
            size="small"
          />
          <TextField
            label="Apellido Materno"
            value={formData.insured.secondLastName}
            onChange={(e) => updateInsured({ secondLastName: e.target.value })}
            size="small"
          />
        </Box>

        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Contacto
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Celular"
            placeholder="912345678"
            value={formData.insured.phone}
            onChange={(e) => {
              updateInsured({ phone: normalizeChileanPhone(e.target.value) });
              setErrors((p) => ({ ...p, phone: '' }));
            }}
            error={!!errors.phone}
            helperText={errors.phone}
            size="small"
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">+56</InputAdornment>,
                inputProps: { maxLength: 9, inputMode: 'numeric' },
              },
            }}
          />
          <TextField
            label="Email"
            type="email"
            value={formData.insured.email}
            onChange={(e) => {
              updateInsured({ email: e.target.value });
              setErrors((p) => ({ ...p, email: '' }));
            }}
            error={!!errors.email}
            size="small"
          />
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
        <Button variant="contained" onClick={handleSubmit} endIcon={<NavigateNextIcon />} sx={primaryButtonSx}>
          Siguiente
        </Button>
      </Box>
    </Box>
  );
};

export default VehicleAndInsuredStep;
